import { Banner, Box, Divider, Flex, Heading, Link, Stack, TextLabel } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useVideoProgress } from '../hooks/use-video-progress';
import type { Video } from '../types';
import { VideoPreview } from './video-preview';
import { WistiaEmbed } from './wistia-embed';

interface VideoViewerProps {
  videoId: string;
  videos: Video[];
  onSelectVideo: (videoId: string) => void;
  onViewAll: () => void;
}

export function VideoViewer({ videoId, videos, onSelectVideo, onViewAll }: VideoViewerProps) {
  const { track } = useAnalytics();
  const { getWatchedStartedCounts } = useVideoProgress();

  const currentIndex = videos.findIndex((v) => v.id === videoId);
  const currentVideo = videos[currentIndex];
  const previousVideo = currentIndex > 0 ? videos[currentIndex - 1] : null;
  const nextVideo = currentIndex < videos.length - 1 ? videos[currentIndex + 1] : null;

  const handleVideoStart = () => {
    const { watched, started } = getWatchedStartedCounts(videos);
    void track('customer_io_basics', 'video_started', {
      id: currentVideo.id,
      name: currentVideo.title,
      watched,
      started,
      total: videos.length,
    });
  };

  const handleVideoEnd = () => {
    const { watched, started } = getWatchedStartedCounts(videos);
    void track('customer_io_basics', 'video_watched', {
      id: currentVideo.id,
      name: currentVideo.title,
      watched,
      started,
      total: videos.length,
    });
  };

  const handleVideoPause = (pauseTime: number) => {
    const { watched, started } = getWatchedStartedCounts(videos);
    void track('customer_io_basics', 'video_stopped', {
      id: currentVideo.id,
      name: currentVideo.title,
      pauseTime,
      watched,
      started,
      total: videos.length,
    });
  };

  const handleVideoSeek = (toTime: number, fromTime: number) => {
    void track('customer_io_basics', 'video_time_shifted', {
      id: currentVideo.id,
      name: currentVideo.title,
      seekTo: toTime,
      seekFrom: fromTime,
    });
  };

  const handlePreviousClick = () => {
    if (previousVideo == null) return;
    void track('customer_io_basics', 'previous_clicked', { id: previousVideo.id });
    onSelectVideo(previousVideo.id);
  };

  const handleNextClick = () => {
    if (nextVideo == null) return;
    void track('customer_io_basics', 'next_clicked', { id: nextVideo.id });
    onSelectVideo(nextVideo.id);
  };

  if (currentVideo == null) {
    return (
      <Stack gap="100">
        <Box>
          <Link
            icon="chevron-left"
            aria-label="Back to all videos"
            onClick={onViewAll}
            data-test-video-library-view-all
          >
            All Videos
          </Link>
        </Box>
        <Banner
          title="No video found"
          variant="error"
          data-test-video-library-invalid-id
          description="Whoops! There was a problem loading this video. Please try again later!"
        />
      </Stack>
    );
  }

  return (
    <Stack gap="100">
      <Box>
        <Link icon="chevron-left" aria-label="Back to all videos" onClick={onViewAll} data-test-video-library-view-all>
          All Videos
        </Link>
      </Box>

      <Heading level="1">{currentVideo.title}</Heading>

      <Box mb="400">
        <WistiaEmbed
          id={currentVideo.id}
          onStart={handleVideoStart}
          onEnd={handleVideoEnd}
          onPause={handleVideoPause}
          onSeek={handleVideoSeek}
        />
      </Box>

      <Divider />

      <Flex justifyContent="space-between" py="100" gap="100">
        {previousVideo != null ? (
          <Stack gap="050" flex={1}>
            <TextLabel py="100">Previous</TextLabel>
            <VideoPreview video={previousVideo} direction="row" onSelect={handlePreviousClick} />
          </Stack>
        ) : null}

        {nextVideo != null ? (
          <Stack gap="050" flex={1}>
            <TextLabel py="100">Up Next</TextLabel>
            <VideoPreview video={nextVideo} direction="row" onSelect={handleNextClick} />
          </Stack>
        ) : null}
      </Flex>
    </Stack>
  );
}
